import {RequestInfo} from "@khan/shared-core";

// The ID of the Chrome extension.
export const KHANMIGO_CHROME_EXTENSION_ID = "adafcnlocfgeglgghjoklnpbdaondoen";

let _chromeMessagePort: chrome.runtime.Port | undefined = undefined;

/**
 * Get the Chrome extension message port for bi-directional messaging with the
 * extension, if it is available.
 *
 * @returns The Chrome extension message port if it is available; `undefined`
 * otherwise.
 */
export const getChromeMessagePort = (): chrome.runtime.Port | undefined => {
    if (!canUseChromeMessagePort()) {
        return;
    }

    // We are accessing the location via window here because we're often not in
    // a scope where use/withLocation can work easily and we know this isn't
    // used in SSR.
    // eslint-disable-next-line no-restricted-syntax -- see above
    const params = new URLSearchParams(window.location.search);
    const tabId = params.get("tabId");

    // Try and connect to our extension. If it isn't there, this should be a
    // no-op. Since we're connecting to a specific extension ID, Chrome won't
    // connect to anyone else. This should prevent other extensions or any
    // sites from grabbing our messages.
    _chromeMessagePort =
        _chromeMessagePort ??
        chrome.runtime.connect(KHANMIGO_CHROME_EXTENSION_ID, {
            // We use the tabId as the name of the port so that the extension can
            // identify the related tab in the browser when we send signals
            // relating to tab content.
            name: `tabId:${tabId}`,
        });

    return _chromeMessagePort;
};

export const canUseChromeMessagePort = (): boolean => {
    // Check if we're in a chrome extension and have access to sendMessage.
    // This should only be defined if an extension has requested and been
    // granted access for messaging to our domain.
    if (
        !_chromeMessagePort &&
        (!RequestInfo.isExtension ||
            typeof chrome === "undefined" ||
            !chrome.runtime?.connect)
    ) {
        return false;
    }

    return true;
};

/**
 * Reset the Chrome message port.
 *
 * This is used for testing.
 */
export const resetChromeMessagePort = (
    portValue?: chrome.runtime.Port,
): void => {
    _chromeMessagePort = portValue;
};
